import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  HostListener,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  IFormConfig,
  IFormFieldConfig,
  IFormFieldHint,
  IFormSection,
} from './models/formConfig.model';
import { extractFormControl } from './utils/generateForm';
import { generateForm } from './utils/generateForm';
import { getFormControl } from './utils/generateForm';
import { PmFormFieldComponent } from './pm-form-field/pm-form-field.component';
import {
  ReactiveFormsModule,
  FormControl,
  AbstractControl,
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  merge,
  Subscription,
  tap,
} from 'rxjs';
import { UiService } from '@shared/services/ui.service';
import { validateAllFormFields } from './utils/validateAllFormFields';
import { PmErrorMessageComponent } from '../pm-error-message/pm-error-message.component';

import { PmMobileFormFieldComponent } from './pm-mobile-form-field/pm-mobile-form-field.component';
import { v4 as uuidv4 } from 'uuid';
@Component({
  standalone: true,
  selector: 'pm-form',
  imports: [
    CommonModule,
    PmFormFieldComponent,
    ReactiveFormsModule,
    PmMobileFormFieldComponent,
    PmErrorMessageComponent,
  ],
  templateUrl: './pm-form.component.html',
  styleUrl: './pm-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PmFormComponent implements OnInit, OnDestroy {
  @ContentChildren(TemplateRef) templates!: QueryList<TemplateRef<any>>;
  @Input({ required: true }) config!: IFormConfig;
  @Input() filtersForm = false;
  @Input() expanded = true;
  @Input() mobile = false;
  @Input() autocomplete = false;
  @Output() submitEvent: EventEmitter<any>;
  private subscriptions = new Subscription();
  form!: FormGroup;
  protected errors: WritableSignal<string[]> = signal([]);
  protected uuid = uuidv4();

  extractFormControl = extractFormControl;
  getFormControl = getFormControl;

  public readonly buttonDisabled: WritableSignal<boolean> = signal(false);

  @HostListener('document:keyup.enter', ['$event'] as any) onKeydownHandler() {
    this.onSubmit();
  }

  get formSections() {
    return this.config.sections;
  }

  formFieldsRendered = signal(true);

  constructor(
    @Inject(UiService) protected uIService: UiService,
    private injector: Injector,
    public readonly _cdr: ChangeDetectorRef
  ) {
    this.submitEvent = new EventEmitter();
  }

  ngOnInit(): void {
    this.initForm();
  }

  getTemplate(
    position: 'before' | 'after',
    fieldName: string
  ): TemplateRef<any> | null {
    return (
      this.templates?.find((tpl: any) =>
        tpl?._declarationTContainer?.localNames?.includes(
          `${position}_${fieldName}`
        )
      ) || null
    );
  }

  toControl(absCtrl: AbstractControl): FormControl {
    const ctrl = absCtrl as FormControl;
    return ctrl;
  }

  private initForm() {
    const generationRes = generateForm(this.config, this.injector);
    this.form = generationRes.form;
    const formValueChangesSub = this.form.statusChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.buttonDisabled.set(!this.form.valid);
        }),
        debounceTime(500),
        tap(() => {
          this._cdr.detectChanges();
        })
      )
      .subscribe();
    this.subscriptions.add(formValueChangesSub);
    this.buttonDisabled.set(!this.form.valid);
    if (generationRes.displayConditionsChanges?.length) {
      const dcc = merge(...generationRes.displayConditionsChanges)
        .pipe(
          tap(() => {
            this._cdr.detectChanges();
          })
        )
        .subscribe();
      this.subscriptions.add(dcc);
    }
  }

  onSubmit() {
    validateAllFormFields(this.form);
    if (this.form.invalid) return;
    let formValues = {};
    if (this.config.rawValues) {
      formValues = this.form.getRawValue();
    } else {
      Object.keys(this.form.value).forEach((key) => {
        formValues = { ...formValues, ...this.form.value[key] };
      });
    }
    this.submitEvent.emit(formValues);
  }

  resetForm() {
    this.form.reset();
  }

  getFieldsOfFormGroup(group: IFormSection) {
    return group.fields.filter((f) =>
      this.shouldDisplayField(f, group.groupName)
    );
  }

  getFormFields(formSection: IFormSection) {
    const fields = [...formSection.fields].filter((f) =>
      this.shouldDisplayField(f, formSection.groupName)
    );
    if (this.expanded) {
      // return formSection.fields;
      return fields;
    } else {
      // if (formSection.fields.some((f) => f.cols === undefined)) return formSection.fields;
      if (formSection.fields.some((f) => f.cols === undefined)) return fields;
      const max = 12;
      let currentWidth = 0;

      let lastIndex;
      // for (let i = 0; i < formSection.fields?.length; i++) {
      //   currentWidth += formSection.fields[i].cols!;
      for (let i = 0; i < fields?.length; i++) {
        currentWidth += fields[i].cols!;
        lastIndex = i;
        if (currentWidth >= max) break;
      }
      if (lastIndex !== undefined && Number.isInteger(lastIndex)) {
        // return [...formSection.fields].splice(0, lastIndex + 1);
        return [...fields].splice(0, lastIndex + 1);
      } else {
        // return formSection.fields;
        return fields;
      }
    }
  }

  shouldDisplayField(field: IFormFieldConfig, groupName: string) {
    let shouldDisplay = true;
    if (!field?.displayConditions || field?.displayConditions?.length === 0)
      return shouldDisplay;

    shouldDisplay = field.displayConditions.every((condition) => {
      //TODO IDisplayCondition[];
      const ctrl = this.extractFormControl(condition.field, this.form);
      if (!ctrl) return;

      return (
        (ctrl as any)?.[condition.controlField] === condition.expectedValue
      );
    });

    if (!shouldDisplay) {
      this.form.get(`${groupName}.${field.name}`)?.reset();
      // this.form.get(`${groupName}.${field.name}`)?.disable(); //TODO TO POWODUJE BŁĄD;
    } else {
      // this.form.get(`${groupName}.${field.name}`)?.enable(); //TODO TO POWODUJE BŁĄD;
    }

    return shouldDisplay;
  }

  shouldDisplayGroup(section: IFormSection) {
    let shouldDisplay = true;
    if (!section?.displayConditions || section?.displayConditions?.length === 0)
      return shouldDisplay;

    shouldDisplay = section.displayConditions.every((condition) => {
      //TODO IDisplayCondition[];
      const field = this.extractFormControl(condition.field, this.form);
      if (!field) return false;
      return field.value === condition.expectedValue;
    });

    if (!shouldDisplay) {
      this.form.get(section.groupName)?.reset();
      this.form.get(section.groupName)?.disable();
    } else {
      this.form.get(section.groupName)?.enable();
    }

    return shouldDisplay;
  }

  setError(error: string) {
    this.errors.update((items) => Array.from(new Set([...items, error])));
  }

  clearErrors() {
    this.errors.set([]);
  }

  disableForm() {
    this.form.disable();
  }

  enableForm() {
    this.form.enable();
  }

  setFieldHint(field: string, hint: IFormFieldHint | null) {
    const control = this.form.get(field);
    if (
      (control as any)?.hint &&
      typeof (control as any)?.hint.set === 'function'
    ) {
      (control as any)?.hint.set(hint);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
